/**
 * kevia-memory.js
 *
 * Capa de memoria pastoral de Kevia.
 * Arquitectura local-first preparada para futura memoria semántica (Plus).
 *
 * Almacena por entidad:
 *   - ts              : última interacción
 *   - interactions    : contador de visitas Kevia
 *   - concerns        : señales recurrentes detectadas
 *   - milestones      : hitos espirituales registrados
 *   - conversations   : últimas 3 conversaciones (resúmenes)
 *   - trajectory      : tendencia pastoral observada
 *
 * Interfaz preparada para:
 *   - embeddings (campo `embedding` en conversaciones)
 *   - retrieval semántico (búsqueda por similaridad)
 *   - continuidad a largo plazo
 */

const MEMORY_KEY        = "kevia_entity_memory";
const TTL_MS            = 90 * 24 * 60 * 60 * 1000; // 90 días
const MAX_CONVS         = 3;
const INTERACTION_DEBOUNCE_MS = 5 * 60 * 1000;      // 5 min — don't count same-session re-opens
const CONCERN_DECAY_MS        = 30 * 24 * 60 * 60 * 1000; // 30 días sin refrescarse → caducan

/* ─── STORAGE ────────────────────────────────────────────────────────────── */

function loadStore() {
  try {
    const raw = localStorage.getItem(MEMORY_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveStore(store) {
  try { localStorage.setItem(MEMORY_KEY, JSON.stringify(store)); }
  catch { /* quota o private browsing */ }
}

function entityKey(type, id) { return `${type}:${id}`; }

function getRecord(store, type, id) {
  return store[entityKey(type, id)] ?? null;
}

function setRecord(store, type, id, record) {
  store[entityKey(type, id)] = record;
}

function prune(store) {
  const now = Date.now();
  for (const k in store) {
    const rec = store[k];
    if (now - (rec.ts ?? 0) > TTL_MS) {
      delete store[k];
      continue;
    }
    // Decay concerns that haven't been refreshed in 30 days
    if (Array.isArray(rec.concerns) && rec.concerns.length > 0) {
      const concernTs = rec.concernTs ?? rec.ts ?? 0;
      if (now - concernTs > CONCERN_DECAY_MS) {
        store[k] = { ...rec, concerns: [] };
      }
    }
  }
}

/* ─── WRITE API ──────────────────────────────────────────────────────────── */

/**
 * Registra que el pastor abrió Kevia mientras veía esta entidad.
 * Llamado automáticamente por useRegisterKeviaContext.
 */
export function recordEntityInteraction(type, id, name) {
  if (!type || !id) return;
  try {
    const store = loadStore();
    const prev  = getRecord(store, type, id) ?? {};
    const now   = Date.now();
    // Don't increment counter if same entity was opened < 5 min ago (navigation noise)
    const recentEnough = now - (prev.ts ?? 0) < INTERACTION_DEBOUNCE_MS;
    setRecord(store, type, id, {
      ...prev,
      type, id, name,
      ts:            now,
      interactions:  recentEnough ? (prev.interactions ?? 1) : (prev.interactions ?? 0) + 1,
      concerns:      prev.concerns      ?? [],
      milestones:    prev.milestones    ?? [],
      conversations: prev.conversations ?? [],
      trajectory:    prev.trajectory    ?? "unknown",
    });
    prune(store);
    saveStore(store);
  } catch { /* silent */ }
}

/**
 * Guarda el resumen de una conversación finalizada.
 * En el futuro este campo tendrá campo `embedding` para retrieval semántico.
 *
 * @param {string} type
 * @param {string} id
 * @param {string} summary        — resumen breve (max 120 chars recomendado)
 * @param {string} tone           — tono de la conversación
 * @param {string|null} conversationId — id de la conversación (para pruning posterior)
 */
export function saveConversationSummary(type, id, summary, tone = "neutral", conversationId = null) {
  if (!type || !id || !summary?.trim()) return;
  try {
    const store = loadStore();
    const prev  = getRecord(store, type, id);
    if (!prev) return;
    const trimmed = summary.trim().slice(0, 120);
    // Skip if identical or very similar to most recent summary (dedup)
    const last = prev.conversations?.[0]?.summary ?? "";
    if (last && (last === trimmed || last.startsWith(trimmed.slice(0, 40)))) return;
    const entry = {
      id:             `conv_${Date.now()}`,
      summary:        trimmed,
      tone,
      ts:             Date.now(),
      conversationId: conversationId ?? null,
    };
    const conversations = [entry, ...(prev.conversations ?? [])].slice(0, MAX_CONVS);
    setRecord(store, type, id, { ...prev, conversations });
    saveStore(store);
  } catch { /* silent */ }
}

/**
 * Elimina todos los resúmenes de conversación vinculados a un conversationId dado.
 * Preserva entradas sin conversationId (registros históricos sin campo).
 *
 * @param {string} conversationId
 */
export function pruneMemoryByConversation(conversationId) {
  if (!conversationId) return;
  try {
    const store = loadStore();
    let changed = false;
    for (const k in store) {
      const rec = store[k];
      if (!Array.isArray(rec.conversations) || rec.conversations.length === 0) continue;
      const filtered = rec.conversations.filter((c) => !c.conversationId || c.conversationId !== conversationId);
      if (filtered.length !== rec.conversations.length) {
        store[k] = { ...rec, conversations: filtered };
        changed = true;
      }
    }
    if (changed) saveStore(store);
  } catch { /* silent */ }
}

/**
 * Registra un hito espiritual (bautismo, membresía, etc.).
 * @param {string} milestone — ej: "bautizado", "primer_visita", "liderazgo"
 */
export function addMilestone(type, id, milestone) {
  if (!type || !id || !milestone) return;
  try {
    const store = loadStore();
    const prev  = getRecord(store, type, id);
    if (!prev) return;
    const milestones = [...new Set([...(prev.milestones ?? []), milestone])];
    setRecord(store, type, id, { ...prev, milestones });
    saveStore(store);
  } catch { /* silent */ }
}

/**
 * Registra una señal de preocupación pastoral recurrente.
 * @param {string} concern — ej: "visit_gap", "urgent_prayer", "baptism_stalled"
 */
export function recordConcern(type, id, concern) {
  if (!type || !id || !concern) return;
  try {
    const store = loadStore();
    const prev  = getRecord(store, type, id);
    if (!prev) return;
    const concerns = [...new Set([...(prev.concerns ?? []), concern])].slice(0, 10);
    setRecord(store, type, id, { ...prev, concerns, concernTs: Date.now() });
    saveStore(store);
  } catch { /* silent */ }
}

/**
 * Actualiza la trayectoria pastoral observada.
 * @param {"improving"|"declining"|"stable"|"unknown"} trajectory
 */
export function updateTrajectory(type, id, trajectory) {
  if (!type || !id) return;
  try {
    const store = loadStore();
    const prev  = getRecord(store, type, id);
    if (!prev) return;
    setRecord(store, type, id, { ...prev, trajectory });
    saveStore(store);
  } catch { /* silent */ }
}

/* ─── READ API ───────────────────────────────────────────────────────────── */

export function getEntityLastInteraction(type, id) {
  if (!type || !id) return null;
  try { return getRecord(loadStore(), type, id) ?? null; }
  catch { return null; }
}

/**
 * Genera líneas de memoria enriquecidas para inyectar en el context block.
 * Retorna un array de strings (filtradas, listo para .filter(Boolean)).
 */
export function buildRichMemoryLines(type, id) {
  const mem = getEntityLastInteraction(type, id);
  if (!mem) return [];

  const lines = [];
  const days  = Math.floor((Date.now() - (mem.ts ?? 0)) / (1000 * 60 * 60 * 24));

  // Continuidad — solo si hay distancia temporal (no misma sesión)
  if (days >= 1) {
    lines.push(`  Conversaciones previas con Kevia: ${days === 1 ? "hace 1 día" : `hace ${days} días`} (${mem.interactions ?? 1} sesión${(mem.interactions ?? 1) !== 1 ? "es" : ""})`);
  }

  // Últimas conversaciones (Plus: futuro retrieval semántico)
  if (mem.conversations?.length > 0) {
    const last = mem.conversations[0];
    const convDays = Math.floor((Date.now() - last.ts) / (1000 * 60 * 60 * 24));
    if (convDays < 60) {
      lines.push(`  Último tema tratado con Kevia: "${last.summary}" (hace ${convDays === 0 ? "hoy" : `${convDays}d`})`);
    }
  }

  // Hitos espirituales registrados
  if (mem.milestones?.length > 0) {
    lines.push(`  Hitos registrados: ${mem.milestones.join(", ")}`);
  }

  return lines;
}

// Backwards-compatible alias
export function buildMemoryLine(type, id) {
  const lines = buildRichMemoryLines(type, id);
  return lines[0] ?? null;
}
